import { ReactNode, createContext, useContext } from 'react'
import { twMerge } from 'tailwind-merge'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Card Context for sharing variant styles
const CardContext = createContext<{ variant: 'default' | 'elevated' | 'outlined' }>({ variant: 'default' })

// ============================================================================
// Card
// ============================================================================
interface CardProps {
  children: ReactNode
  className?: string
  variant?: 'default' | 'elevated' | 'outlined'
}

export const Card = ({ children, className, variant = 'default' }: CardProps) => {
  const variantStyles = {
    default: 'bg-white border border-slate-200/80 shadow-sm',
    elevated: 'bg-white shadow-lg shadow-slate-200/50 border border-slate-100',
    outlined: 'bg-white border-2 border-slate-200'
  }

  return (
    <CardContext.Provider value={{ variant }}>
      <div className={twMerge('rounded-xl overflow-hidden', variantStyles[variant], className)}>{children}</div>
    </CardContext.Provider>
  )
}

// ============================================================================
// CardHeader
// ============================================================================
interface CardHeaderProps {
  children?: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  icon?: IconDefinition
  actions?: ReactNode
  className?: string
  noBorder?: boolean
}

export const CardHeader = ({
  children,
  title,
  subtitle,
  icon,
  actions,
  className,
  noBorder = false
}: CardHeaderProps) => {
  const { variant } = useContext(CardContext)

  // If children provided, render them directly
  if (children) {
    return (
      <div className={twMerge('px-6 py-4', !noBorder && 'border-b border-slate-200/80', className)}>{children}</div>
    )
  }

  return (
    <div className={twMerge('px-6 py-4', !noBorder && 'border-b border-slate-200/80', className)}>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex items-start gap-3 min-w-0'>
          {icon && (
            <div
              className={twMerge(
                'flex items-center justify-center w-10 h-10 rounded-lg shrink-0',
                variant === 'elevated'
                  ? 'bg-linear-to-br from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-200'
                  : 'bg-slate-100 text-slate-600'
              )}
            >
              <FontAwesomeIcon icon={icon} className='text-lg' />
            </div>
          )}
          <div className='min-w-0'>
            {title && <h3 className='text-lg font-semibold text-slate-900 truncate'>{title}</h3>}
            {subtitle && <p className='text-sm text-slate-500 mt-0.5'>{subtitle}</p>}
          </div>
        </div>
        {actions && <div className='flex items-center gap-2 shrink-0'>{actions}</div>}
      </div>
    </div>
  )
}

// ============================================================================
// CardBody
// ============================================================================
interface CardBodyProps {
  children: ReactNode
  className?: string
  noPadding?: boolean
}

export const CardBody = ({ children, className, noPadding = false }: CardBodyProps) => {
  return <div className={twMerge(!noPadding && 'p-6', className)}>{children}</div>
}

// ============================================================================
// CardFooter
// ============================================================================
interface CardFooterProps {
  children: ReactNode
  className?: string
  noBorder?: boolean
}

export const CardFooter = ({ children, className, noBorder = false }: CardFooterProps) => {
  return (
    <div className={twMerge('px-6 py-4 bg-slate-50/50', !noBorder && 'border-t border-slate-200/80', className)}>
      {children}
    </div>
  )
}

export default Card
